import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, LOCALE_ID, Injector } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { routes } from './app.routes';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import it from '@angular/common/locales/it';
import { environment } from '../environments/default/environment';
import { ApiPurpleFitCompleteRegistrationRequest, ApiPurpleFitRegisterRequest, ApiPurpleFitUpdateUserInfoRequest, PRPVUserFull, PurpleFitUserManagerService } from '../core-modules/sdk';
import { providePurpleFitCore } from './purple-fit-core.module';
import { JwtModule } from '@auth0/angular-jwt';
import * as f from 'date-fns/locale';

import { provideTranslationCore, PURPLE_TRANSLATION_SERVICE_OPTIONS, PurpleTranslationServiceOptions } from '@purple-lib/purple-translations-core';
import { providePurpleApiErrorManager, providePurpleLoader, provideUtilitiesCore, PurpleLogLevel, PurpleStorageService } from '@purple-lib/purple-utilities';
import { provideMediaCore } from '@purple-lib/purple-medias-core';
import { NZ_DATE_CONFIG, NZ_DATE_LOCALE } from 'ng-zorro-antd/i18n';
import { provideNotificationCore } from '@purple-lib/purple-notifications-core';
import { provideAddressCore } from '@purple-lib/purple-addresses-core';
import { provideUserCore, PurpleUserService } from '@purple-lib/purple-users-core';
import { providePoliceCore } from '@purple-lib/purple-policies-core';
import { NZ_I18N, it_IT, en_US } from 'ng-zorro-antd/i18n';
import { providePurpleAdminTool, PURPLE_ADMIN_TOOL_OPTIONS, PurpleAdminToolOptions } from '@purple-lib/purple-admin-tool';

registerLocaleData(it, "it-it");
registerLocaleData(it, "it");
registerLocaleData(en, "en-us");

const apiBasePath = localStorage.getItem('ApiBasePath') ?? environment.API_BASE_PATH;
export function createTranslationServiceOptions(injector: Injector): PurpleTranslationServiceOptions {
  return {
    defaultLanguage: environment.DEFAULT_LANGUAGE,
    editTranslationAffix: environment.EDIT_TRAN_AFFIX,
    loaderId: environment.LOADER_ID,
    get canAddTranslation() {
      try {
        const userSvc = injector.get(PurpleUserService);
        return userSvc.currentUser?.roleLevel == 0;
      } catch {
        return false;
      }
    },
    get canDeleteTranslation() {
      try {
        const userSvc = injector.get(PurpleUserService);
        return userSvc.currentUser?.roleLevel == 0;
      } catch {
        return false;
      }
    },
    get canSyncDefaultTranslations() {
      try {
        const userSvc = injector.get(PurpleUserService);
        return userSvc.currentUser?.roleLevel == 0;
      } catch {
        return false;
      }
    }
  };
}


export function createAdminToolOptions(injector: Injector): PurpleAdminToolOptions {
  return {
    loaderId: environment.LOADER_ID,
    toolButtonPosition: 'top-center',
    get canEditTran() {
      try {
        const userSvc = injector.get(PurpleUserService);
        return userSvc.currentUser?.roleLevel == 0;
      } catch {
        return false;
      }
    },
    get canEditMedias() {
      try {
        const userSvc = injector.get(PurpleUserService);
        return userSvc.currentUser?.roleLevel == 0;
      } catch {
        return false;
      }
    },
    get canSyncTran() {
      try {
        const userSvc = injector.get(PurpleUserService);
        return userSvc.currentUser?.roleLevel == 0;
      } catch {
        return false;
      }
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    {
      provide: LOCALE_ID,
      deps: [PurpleStorageService],
      useFactory: (storageSvc: PurpleStorageService) => storageSvc.get<string>("Language") ?? "it-it"
    },
    {
      provide: NZ_I18N,
      useFactory: (localId: string) => {
        switch (localId) {
          case 'en-us':
          case 'en-gb':
            return en_US;
          case 'it-it':
          case 'it':
            return it_IT;
          default:
            return it_IT;
        }
      },
      deps: [LOCALE_ID]
    },
    {
      provide: NZ_DATE_CONFIG,
      useFactory: (localId: string) => {
        let itDate = f.it.options?.weekStartsOn ?? 1;
        switch (localId) {
          case 'en-us':
            return {
              firstDayOfWeek: f.enUS.options?.weekStartsOn ?? 0,
            };
          case 'en-gn':
            return {
              firstDayOfWeek: f.enUS.options?.weekStartsOn ?? 0,
            };
          case 'it-it':
            return {
              firstDayOfWeek: itDate,
            };
          case 'it':
            return {
              firstDayOfWeek: itDate,
            };
          default:
            return {
              firstDayOfWeek: itDate,
            };
        }
      },
      deps: [LOCALE_ID]
    },
    {
      provide: NZ_DATE_LOCALE, useFactory: (localId: string) => {
        switch (localId) {
          case 'en-us':
            return f.enUS;
          case 'en-gb':
            return f.enGB;
          case 'it-it':
            return f.it;
          case 'it':
            return f.it;
          default:
            return f.it;
        }
      },
      deps: [LOCALE_ID]
    },
    ...provideUtilitiesCore(undefined,undefined,{
      isProduction: environment.production,
      minLevel:PurpleLogLevel.Warning
    }),

    ...provideMediaCore(apiBasePath),
    ...provideNotificationCore(apiBasePath),
    ...provideAddressCore(apiBasePath),


    ...provideTranslationCore(apiBasePath, "it-it"),
    {
      provide: PURPLE_TRANSLATION_SERVICE_OPTIONS,
      useFactory: createTranslationServiceOptions,
      deps: [Injector]
    },

    ...providePurpleAdminTool(),
    {
      provide: PURPLE_ADMIN_TOOL_OPTIONS,
      useFactory: createAdminToolOptions,
      deps: [Injector]
    },
    ...provideUserCore<PRPVUserFull, ApiPurpleFitRegisterRequest, ApiPurpleFitCompleteRegistrationRequest, ApiPurpleFitUpdateUserInfoRequest, PurpleFitUserManagerService>(apiBasePath, PurpleFitUserManagerService),
    ...providePoliceCore(apiBasePath),

    ...providePurpleFitCore(apiBasePath),
    
    ...providePurpleLoader(),
    ...providePurpleApiErrorManager(),//Importa HTTPCLIENT + INTERCEPTOR
    importProvidersFrom(
      JwtModule.forRoot({})
    ),

    provideAnimationsAsync()
  ]
};
